import React, { useEffect, useMemo, useState } from "react";
import { VendorItemDetails } from "types/vendor";
import { updateVendorItemQuantity, removeVendorItemDetails } from "api/vendorsApi";
import { Button } from "components/button";
import { useTranslation } from "react-i18next";
import { isStaleReading } from "utils/heritageStaleReading";
import { useQueryClient } from "react-query";
import { useModalContext } from "components/modal";
import { toast } from "react-toastify";
import styles from "./VendorItemDetail.module.scss";
import VendorLeadTimeInput from "views/vendors/components/VendorLeadTimeInput";
import VendorItemNotesInput from "views/vendors/components/VendorItemNotesInput";
import VendorItemPriceInput from "views/vendors/components/VendorItemPriceInput";
import { clampVendorItemNotes } from "views/vendors/constants/vendorItemFields";
import { normalizeLeadTimeStored } from "views/vendors/constants/vendorLeadTime";
import {
  formatCentsToVendorPriceInput,
  isVendorPriceInputValid,
  parseVendorPriceInputToCents,
} from "views/vendors/constants/vendorItemPrice";

interface IProps {
  item: VendorItemDetails;
  vendorId?: number;
}

const VendorItemDetail = ({ item, vendorId }: IProps) => {
  const { t } = useTranslation();
  const tr = (key: string) =>
    t(`views.vendors.VendorBody.VendorItems.VendorItemDetail.VendorItemDetail.${key}`);
  
  const queryClient = useQueryClient();
  const { setModal } = useModalContext();

  // Destructure fields
  const {
    id,
    vendor_part_number,
    price,
    item_name,
    long_description,
    model,
    imageUrl,
    heritage_image_url,
    last_update,
    stock_level,
    stock_branch_label,
    latest_status,
    siteone_description,
    siteone_item_number,
    siteone_price,
    is_heritage_vendor,
    lead_time,
    notes,
  } = item;

  const isSiteoneLinked = siteone_item_number !== null;
  // #13 — a Heritage-integrated vendor's price is owned by the integration, so
  // the whole row is read-only here, mirroring the SiteOne lock. The API also
  // rejects the edit, so this only spares the user a doomed save.
  const isHeritageLinked = is_heritage_vendor === true;
  const isIntegrationLocked = isSiteoneLinked || isHeritageLinked;

  // Use fallback logic
  // const description = siteone_description || long_description;

  const storedPriceCents = siteone_price ?? price ?? null;
  const storedVendorPartNumber =
    (siteone_item_number || vendor_part_number || "").trim();

  const [newVendorPartNumber, setNewVendorPartNumber] = useState(
    siteone_item_number || vendor_part_number || ""
  );
  const [savedVendorPartNumber, setSavedVendorPartNumber] = useState(
    storedVendorPartNumber
  );
  const [savedPriceCents, setSavedPriceCents] = useState<number | null>(storedPriceCents);
  const [newPrice, setNewPrice] = useState(() =>
    formatCentsToVendorPriceInput(storedPriceCents)
  );
  const initialLeadTime = normalizeLeadTimeStored(lead_time);
  const [savedLeadTime, setSavedLeadTime] = useState(initialLeadTime);
  const [newLeadTime, setNewLeadTime] = useState(initialLeadTime);
  const [savedNotes, setSavedNotes] = useState((notes ?? "").trim());
  const [newNotes, setNewNotes] = useState(notes ?? "");
  const [error, setError] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);

  useEffect(() => {
    const cents = siteone_price ?? price ?? null;
    const part = (siteone_item_number || vendor_part_number || "").trim();
    setSavedPriceCents(cents);
    setNewPrice(formatCentsToVendorPriceInput(cents));
    setSavedVendorPartNumber(part);
    setNewVendorPartNumber(siteone_item_number || vendor_part_number || "");
    const normalizedLead = normalizeLeadTimeStored(lead_time);
    setSavedLeadTime(normalizedLead);
    setNewLeadTime(normalizedLead);
    setSavedNotes((notes ?? "").trim());
    setNewNotes(notes ?? "");
    setError(false);
  }, [
    id,
    siteone_price,
    price,
    siteone_item_number,
    vendor_part_number,
    lead_time,
    notes,
  ]);

  const isPriceDirty = useMemo(() => {
    return parseVendorPriceInputToCents(newPrice) !== (savedPriceCents ?? null);
  }, [newPrice, savedPriceCents]);

  const isPartNumberDirty = useMemo(() => {
    return newVendorPartNumber.trim() !== savedVendorPartNumber;
  }, [newVendorPartNumber, savedVendorPartNumber]);

  const normalizedLeadTime = (value: string) => normalizeLeadTimeStored(value);

  const isLeadTimeDirty = useMemo(() => {
    return normalizedLeadTime(newLeadTime) !== normalizedLeadTime(savedLeadTime);
  }, [newLeadTime, savedLeadTime]);

  const isNotesDirty = useMemo(() => {
    return newNotes.trim() !== savedNotes;
  }, [newNotes, savedNotes]);

  const priceInputInvalid = useMemo(() => {
    const trimmed = newPrice.trim();
    return trimmed !== "" && !isVendorPriceInputValid(newPrice);
  }, [newPrice]);

  const effectivePriceCents = useMemo(() => {
    return parseVendorPriceInputToCents(newPrice) ?? savedPriceCents;
  }, [newPrice, savedPriceCents]);

  const canSave =
    !isSiteoneLinked &&
    !isHeritageLinked &&
    siteone_price === null &&
    !priceInputInvalid &&
    effectivePriceCents != null &&
    (isPriceDirty ||
      isPartNumberDirty ||
      isLeadTimeDirty ||
      isNotesDirty);

  const handleSave = async () => {
    if (!canSave) return;
    const priceCents = effectivePriceCents;
    if (priceCents == null) return;

    try {
      const response = await updateVendorItemQuantity(
        id,
        priceCents,
        newVendorPartNumber.trim() || null,
        normalizeLeadTimeStored(newLeadTime) || null,
        clampVendorItemNotes(newNotes).trim() || null
      );

      if (response) {
        const cents = response.price ?? priceCents;
        setSavedPriceCents(cents);
        setNewPrice(formatCentsToVendorPriceInput(cents));
        setSavedVendorPartNumber(newVendorPartNumber.trim());
        const savedLead = normalizeLeadTimeStored(newLeadTime);
        setSavedLeadTime(savedLead);
        setNewLeadTime(savedLead);
        setSavedNotes(newNotes.trim());
        queryClient.invalidateQueries(["vendors_items", vendorId]);
        toast.success(tr("Changes saved"));
      }
    } catch (saveError) {
      console.error("Error saving vendor item price:", saveError);
      toast.error(tr("Error saving price"));
    }
  };

  const handleRemove = async () => {
    try {
      await removeVendorItemDetails(id);
      // Refresh the vendor items list
      queryClient.invalidateQueries(["vendors_items", vendorId]);
      toast.success(tr("Item removed successfully"));
    } catch (error) {
      console.error("Error removing item:", error);
      toast.error(tr("Error removing item"));
    }
  };


  const showRemoveConfirmation = () => {
    setModal({
      component: (
        <div className="">
          {/* Content */}
          <div className="px-6 py-4">
            <p className="text-gray-700 leading-relaxed">
              {tr("Are you sure you want to remove this item from the vendor?")}
            </p>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              primary
              onClick={() => {
                setModal();
                handleRemove();
              }}
              // className="eserv-btn eserv-btn-danger"
            >
              {tr("Remove")}
            </Button>
            <Button
              onClick={() => setModal()}
              // className="eserv-btn eserv-btn-secondary"
            >
              {tr("Cancel")}
            </Button>
          </div>
        </div>
      ),
      label: tr("Remove Item"),
    });
  };


  return (
    <div
      className={`flex items-start justify-between gap-4 bg-gray-100 rounded border p-4 mb-3 ${
        notesExpanded ? styles.itemRowNotesExpanded : ""
      }`}
    >
      {/* Local image first; Heritage's product image fills the gap. The API
          only sends heritage_image_url for Heritage-integrated vendors, so
          rendering it here stays within the licensed screens. */}
      {(imageUrl || heritage_image_url) && (
        <img
          src={imageUrl || heritage_image_url || undefined}
          alt={item_name}
          className="w-16 h-16 object-contain rounded border"
        />
      )}

      <div className="flex flex-col flex-1 min-w-0">
        <div className="text-sm font-semibold text-gray-700">
          {/* F1 — Sanjay's ★: this part is in your items (the downloaded-but-never-
              ordered rows live in the section below, without one). */}
          {vendor_part_number && (
            <span
              className="mr-1 text-amber-500"
              role="img"
              aria-label={tr("In your items")}
              title={tr("In your items")}
            >
              ★
            </span>
          )}
          {model}
          {vendor_part_number && (
            <span className="ml-1 text-xs text-gray-500">
              (vend.: {vendor_part_number})
            </span>
          )}
        </div>
        <div className="text-md font-bold text-gray-900">{siteone_item_number || item_name}</div>
        <div className="text-sm text-gray-600 truncate">{siteone_description || long_description}</div>
        <div></div>
          {siteone_description && (
            <div className="bg-[#79ff79] rounded-[5px] w-fit text-center px-[5px] py-[5px] font-bold border-[2px] border-black">
              {tr("Linked SiteOne Item")}
            </div>
          )}
          {isHeritageLinked && (
            <div className="bg-[#bcd6ff] rounded-[5px] w-fit text-center px-[5px] py-[5px] font-bold border-[2px] border-black">
              {tr("Price managed by Heritage")}
            </div>
          )}
      </div>

      <div className="flex flex-col items-end gap-2 text-xs text-right min-w-[520px] max-w-[55%]">

        <div className={styles.fieldsRow}>
          <div className={styles.fieldColumn}>
            <label className={styles.fieldLabel}>
              {isSiteoneLinked ? "SiteOne" : tr("Vendor")} {tr("Part Number")}
            </label>
            <input
              type="text"
              value={newVendorPartNumber}
              onChange={(e) => setNewVendorPartNumber(e.target.value)}
              className={styles.fieldInput}
              disabled={isIntegrationLocked}
            />
          </div>

          <div className={styles.fieldColumn}>
            <label className={styles.fieldLabel}>{tr("Price")}</label>
            <VendorItemPriceInput
              value={newPrice}
              onChange={(value) => {
                setNewPrice(value);
                setError(value.trim() !== "" && !isVendorPriceInputValid(value));
              }}
              className={`${styles.fieldInput} ${error || priceInputInvalid ? styles.fieldInputError : ""}`}
              disabled={siteone_price !== null || isHeritageLinked}
            />
          </div>

          <div className={styles.fieldColumn}>
            <label className={styles.fieldLabel}>{tr("Lead Time")}</label>
            <VendorLeadTimeInput
              value={newLeadTime}
              onChange={setNewLeadTime}
              disabled={isIntegrationLocked}
              className={styles.fieldInput}
            />
          </div>

          <div className={`${styles.fieldColumn} ${styles.notesFieldColumn}`}>
            <label className={styles.fieldLabel}>{tr("Notes")}</label>
            <VendorItemNotesInput
              value={newNotes}
              onChange={setNewNotes}
              onExpandedChange={setNotesExpanded}
              disabled={isIntegrationLocked}
              className={`${styles.fieldInput} ${styles.notesInput}`}
            />
          </div>

          <div className={styles.actionColumn}>
            <label className={styles.fieldLabel} aria-hidden="true">
              &nbsp;
            </label>
            <div className={styles.actionButtons}>
              {!isSiteoneLinked && (
                <Button
                  className={`${styles.actionButton} ${!canSave ? styles.saveButtonFaded : ""}`}
                  primary
                  onClick={handleSave}
                  disabled={!canSave}
                >
                  {tr("Save")}
                </Button>
              )}

              <Button
                className={styles.actionButton}
                style={{ backgroundColor: "red", color: "white" }}
                onClick={showRemoveConfirmation}
              >
                {tr("Remove")}
              </Button>
            </div>
          </div>



        </div>
        {/* #35 — the vendor's stock as last refreshed, with its branch. */}
        {stock_level != null && (
          <div className="text-gray-500">
            <strong>{tr("Vendor stock")}:</strong> {Number(stock_level)}
            {stock_branch_label
              ? ` @ ${stock_branch_label}`
              : ` (${tr("branch unknown")})`}
          </div>
        )}
        {last_update && (
          <div className="text-gray-500">
            <strong>Last Updated:</strong>{" "}
            {new Date(last_update).toLocaleDateString()}
            {isStaleReading(last_update) && (
              <span className="text-amber-600"> ({tr("may be out of date")})</span>
            )}
          </div>
        )}
        {latest_status && (
          <div className="text-gray-500">
            <strong>Status:</strong> {latest_status}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorItemDetail;
